// The built-in content library: original graded stories, ready to assign.
//
// Every story here was written for Gibberfish Classroom (none is copied from a
// textbook or website), so any teacher may assign it. Paragraphs are separated
// by a blank line, the same as a teacher's pasted story.
//
// Levels follow the CEFR. A suggested pace (words a minute, see lib/reading.ts)
// and number of reads come with each; the teacher can change both before
// assigning. The texts are loaded only when the Library page opens.

export type Level = 'A1' | 'A2' | 'B1' | 'B2';

export type LibraryStory = {
  id: string;
  lang: string;
  level: Level;
  title: string;
  /** One line, in English, for the teacher. */
  blurb: string;
  /** What it practises, for the filter. */
  tags: string[];
  text: string;
  /** Suggested settings. */
  wpm: number;
  times: number;
};

export const LEVELS: { level: Level; label: string }[] = [
  { level: 'A1', label: 'A1 · Beginner' },
  { level: 'A2', label: 'A2 · Elementary' },
  { level: 'B1', label: 'B1 · Intermediate' },
  { level: 'B2', label: 'B2 · Upper intermediate' },
];

/** Suggested pace and reads by level, for stories that don't set their own. */
export const LEVEL_DEFAULTS: Record<Level, { wpm: number; times: number }> = {
  A1: { wpm: 40, times: 3 },
  A2: { wpm: 55, times: 3 },
  B1: { wpm: 70, times: 2 },
  B2: { wpm: 90, times: 2 },
};

export async function loadLibrary(): Promise<LibraryStory[]> {
  const parts = await Promise.all([
    import('./es').then(m => m.STORIES),
    import('./pt').then(m => m.STORIES),
    import('./en').then(m => m.STORIES),
    import('./fr').then(m => m.STORIES),
  ]);
  return parts.flat();
}

export async function findStory(id: string): Promise<LibraryStory | undefined> {
  return (await loadLibrary()).find(s => s.id === id);
}

/** Shorthand for the story files: level defaults filled in, text trimmed. */
export function story(s: Omit<LibraryStory, 'wpm' | 'times'> & Partial<Pick<LibraryStory, 'wpm' | 'times'>>): LibraryStory {
  return { ...LEVEL_DEFAULTS[s.level], ...s, text: s.text.trim().replace(/\n[ \t]+/g, '\n') };
}
